import React, {useState} from 'react'
import {
  DateTime,
  NoResults,
  SelectUser,
  useLocationCheckpointScans,
  useLocationContext,
  UserName,
  usePaginationState,
  useUrlState,
  Alerts,
  type DownloadPdfResult,
  useDownloadsApiServiceContext,
  useRPCContext,
  locationCheckpointScansSummaryDownload,
  LabeledInput,
  SelectCheckpoint,
  CheckpointName,
  UserAnPCheck,
} from '@therms/react-modules'
import {
  Button,
  ContextMenu,
  DateTimeRangePicker,
  Dropdown,
  Icon,
  Loading,
  Modal,
  Pagination,
  toastManager,
} from '@therms/atalaya'
import {CheckpointScanDetails} from '../CheckpointScanDetails'
import {useUpdateEffect} from 'ahooks'
import {downloadBase64AsPdf} from '@therms/web-js'
import dayjs from 'dayjs'
import classNames from 'classnames'
import {IoMdDownload} from 'react-icons/io'
import {
  MdDescription,
  MdDataset,
  MdRefresh,
  MdOutlineQrCode,
  MdNfc,
  MdGpsFixed,
} from 'react-icons/md'

function CheckpointScansList() {
  const {client} = useDownloadsApiServiceContext()
  const {rpcService} = useRPCContext()
  const {location, locationId} = useLocationContext(true)
  const [showScanIdDetailModal, setShowScanIdDetailModal] = useState<string>()
  const {recordsPerPage, setRecordsPerPage, setSkippedRecords, skippedRecords} =
    usePaginationState('locchkptscnlst', [])

  const [urlState, setUrlState] = useUrlState<{
    checkpointId: undefined
    dateTimeRange: {start?: string; end?: string}
    userId: undefined
  }>('checkpointscanfilters', {
    checkpointId: undefined,
    dateTimeRange: {end: undefined, start: undefined},
    userId: undefined,
  })

  useUpdateEffect(() => {
    setSkippedRecords(0)
  }, [urlState])

  const {data, loading} = useLocationCheckpointScans({
    checkpointIds: urlState.checkpointId ? [urlState.checkpointId] : undefined,
    fromDate: urlState.dateTimeRange.start,
    locationId,
    pagination: {
      limit: recordsPerPage,
      skip: skippedRecords,
    },
    toDate: urlState.dateTimeRange.end,
    userId: urlState.userId,
  })

  const handleCheckpointScansPDFDownload = () =>
    client.locations.DownloadLocationCheckpointScans(
      {
        body: {
          checkpointId: urlState.checkpointId,
          fromDate: urlState.dateTimeRange.start,
          locationId,
          toDate: urlState.dateTimeRange.end,
          userId: urlState.userId,
        },
      },
      {timeout: 60_000},
    )
      .then((response) => {
        const result = response as DownloadPdfResult
        if (result.pdfBase64) {
          downloadBase64AsPdf(
            `${location?.name} Checkpoint Scans - ${dayjs(
              urlState?.dateTimeRange.start,
            ).format()} - ${dayjs(urlState?.dateTimeRange.end).format()}`,
            result.pdfBase64,
          )
        }
      })

  const handleCheckpointScansSummaryDownload = (format: 'csv' | 'excel') =>
    rpcService.Locations.GetLocationCheckpointScansList.v2
      .call(
        {
          checkpointIds: urlState?.checkpointId
            ? [urlState.checkpointId]
            : undefined,
          fromDate: urlState?.dateTimeRange.start,
          locationId,
          pagination: {
            limit: 5000,
          },
          toDate: urlState?.dateTimeRange.end,
          userId: urlState?.userId,
        },
        {
          timeout: 60_000,
        },
      )
      .then((response) => {
        locationCheckpointScansSummaryDownload(
          response.data.checkpointScans,
          null,
          {
            fileName: `${location.name} Checkpoint Scans`,
            format,
          },
        )
      })

  const handleDownload = (format: 'pdf' | 'csv' | 'excel') => {
    const toastId = Alerts.info(
      <div>
        <Icon Icon={MdRefresh} spin /> Starting download, please wait...
      </div>,
      {autoClose: false, position: 'top-center'},
    )

    const request =
      format === 'pdf'
        ? handleCheckpointScansPDFDownload
        : () => handleCheckpointScansSummaryDownload(format)

    request()
      .catch((err: any) => {
        console.error('err downloading entry', err)
      })
      .finally(() => toastManager.dismiss(toastId))
  }

  return (
    <div>
      <div className="gap-xs flex flex-wrap">
        <div className="gap-xxs grid grow grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <div className="col-span-1 md:col-span-2">
            <DateTimeRangePicker
              onChangeValue={(val) => {
                setUrlState((state) => ({
                  ...state,
                  dateTimeRange: val,
                }))
              }}
              onClearValue={() =>
                setUrlState((state) => ({
                  ...state,
                  dateTimeRange: {end: undefined, start: undefined},
                }))
              }
              value={urlState.dateTimeRange}
            />
          </div>

          <div className="col-span-1">
            <LabeledInput
              label="User"
              nameElement={<UserName userId={urlState.userId} />}
              inputElement={
                <SelectUser
                  rolesFilter={['ops']}
                  isClearable
                  menuIsOpen
                  onChangeValue={(val) => {
                    setUrlState((state) => ({
                      ...state,
                      userId: val,
                    }))
                  }}
                  placeholder="User"
                  value={urlState.userId}
                />
              }
              value={urlState.userId}
            />
          </div>

          <div className="col-span-1">
            <LabeledInput
              label="Checkpoint"
              nameElement={
                <CheckpointName checkpointId={urlState.checkpointId || ''} />
              }
              inputElement={
                <SelectCheckpoint
                  isClearable
                  locationId={locationId}
                  menuIsOpen
                  onChangeValue={(val) => {
                    setUrlState((state) => ({...state, checkpointId: val}))
                  }}
                  value={urlState.checkpointId}
                />
              }
              value={urlState.checkpointId}
            />
          </div>
        </div>

        <UserAnPCheck
          check={(user) => user.anp.role.ops.access.canDownloadAndExportData}
        >
          <div className="flex w-full items-center justify-end sm:w-fit">
            <Dropdown
              className="ml-auto"
              controller={({close, isOpen, open}) => (
                <div className="">
                  <Button
                    className="h-full"
                    onClick={isOpen ? close : open}
                    size="sm"
                    outline
                    variant="main"
                    fullWidth
                  >
                    <Icon Icon={IoMdDownload} size="sm" /> Download
                  </Button>
                </div>
              )}
            >
              <ContextMenu.Option onClick={() => handleDownload('pdf')}>
                <div className="min-w-[100px]">
                  <Icon Icon={MdDescription} /> <span>PDF</span>
                </div>
              </ContextMenu.Option>

              <ContextMenu.Divider />

              <ContextMenu.Option onClick={() => handleDownload('csv')}>
                <div className="min-w-[100px]">
                  <Icon Icon={MdDataset} /> <span>CSV</span>
                </div>
              </ContextMenu.Option>

              <ContextMenu.Option onClick={() => handleDownload('excel')}>
                <div className="min-w-[100px]">
                  <Icon Icon={MdDataset} /> <span>Excel</span>
                </div>
              </ContextMenu.Option>
            </Dropdown>
          </div>
        </UserAnPCheck>
      </div>

      {data?.checkpointScans.length === 0 && <NoResults text="No Scans" />}

      <div>
        {loading && (
          <div className="mt-xxl flex justify-center">
            <Loading
              message="Loading Scans..."
              messagePosition="vertical"
              size="lg"
            />
          </div>
        )}

        <div
          className="mt-base grid grid-cols-3 content-center text-sm"
          style={{gridTemplateColumns: 'auto auto 1fr 1fr'}}
        >
          {data?.checkpointScans.map((checkpointScan, index) => {
            const even = index % 2

            return (
              <div
                className="group contents cursor-pointer"
                onClick={() => setShowScanIdDetailModal(checkpointScan.id)}
                key={checkpointScan.id}
              >
                <div
                  className={classNames(
                    {'bg-surface': !even, 'bg-surface-subtle': even},
                    'p-xxs group-hover:bg-surface-strong transition',
                  )}
                >
                  <span className="uppercase">
                    <Icon
                      className="mr-xxs"
                      size="sm"
                      Icon={
                        checkpointScan.scan.checkpointType === 'qr'
                          ? MdOutlineQrCode
                          : MdNfc
                      }
                    />

                    <span className="text-color-subtle text-xs font-medium">
                      {checkpointScan.scan.checkpointType}
                    </span>
                  </span>{' '}
                </div>

                <div
                  className={classNames(
                    {'bg-surface': !even, 'bg-surface-subtle': even},
                    'p-xxs group-hover:bg-surface-strong transition',
                  )}
                >
                  <DateTime
                    timestamp={checkpointScan.scan.timestamp}
                    toggleRelativeOnClick={false}
                  />
                </div>

                <div
                  className={classNames(
                    {'bg-surface': !even, 'bg-surface-subtle': even},
                    'p-xxs text-color-strong group-hover:bg-surface-strong truncate transition',
                  )}
                >
                  {checkpointScan.scan.checkpointName}
                </div>

                <div
                  className={classNames(
                    {'bg-surface': !even, 'bg-surface-subtle': even},
                    'p-xxs group-hover:bg-surface-strong truncate transition',
                  )}
                >
                  <UserName userId={checkpointScan.scan.userId} />

                  {checkpointScan.scan.geo && (
                    <span className="ml-xs opacity-50">
                      {checkpointScan.scan.geo.coords && (
                        <Icon color="main" Icon={MdGpsFixed} size="sm" />
                      )}{' '}
                      {checkpointScan.scan.geo.device.os}{' '}
                      {checkpointScan.scan.geo.device.name}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <Pagination
          className="mt-base"
          onPageClick={setSkippedRecords}
          onSetRecordsPerPage={setRecordsPerPage}
          recordsPerPage={recordsPerPage}
          showDescription
          skippedRecords={skippedRecords}
          totalRecords={data?.total || 0}
        />
      </div>

      {showScanIdDetailModal && (
        <Modal
          closeHandler={() => setShowScanIdDetailModal(undefined)}
          escPressClose
          header="Checkpoint Scan Detail"
        >
          <CheckpointScanDetails checkpointScanId={showScanIdDetailModal} />
        </Modal>
      )}
    </div>
  )
}

export {CheckpointScansList}
